import React, {
    useState, useEffect, useRef, useMemo, useCallback
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';
import * as XLSX from 'xlsx';
import {
    FaPlus, FaTrash, FaSitemap, FaSave, FaTimes, FaDownload, FaUpload,
    FaSearch, FaRegFolder, FaRegFileAlt, FaInfoCircle, FaFileExcel,
    FaLayerGroup, FaCheckCircle, FaExclamationTriangle, FaChartLine,
    FaHistory, FaLink, FaChevronDown, FaChevronRight, FaMagic,
    FaRobot, FaCheck, FaArrowRight, FaPencilAlt, FaRegClock, FaSyncAlt,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import './PlanoContas.css';

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const CONFIDENCE_MAP = {
    alta:  { label: 'Alta',  color: '#10b981', bg: '#d1fae5', dot: '#059669' },
    media: { label: 'Média', color: '#d97706', bg: '#fef3c7', dot: '#b45309' },
    baixa: { label: 'Baixa', color: '#e11d48', bg: '#ffe4e6', dot: '#be123c' },
};
const ORIGEM_LABEL = {
    'despesas_categorias':    { label: 'DESPESA',  bg: '#fee2e2', color: '#991b1b' },
    'receitas_categorias':    { label: 'RECEITA',  bg: '#dcfce7', color: '#166534' },
    'categorias_financeiros': { label: 'FINANCEIRO', bg: '#dbeafe', color: '#1e40af' },
};

// ─────────────────────────────────────────────────────────────────────────────
// ASSISTENTE IA — Drawer de Classificação Contábil
// ─────────────────────────────────────────────────────────────────────────────
function AssistentePainel({ onClose, onAplicado }) {
    const [etapa, setEtapa]           = useState('loading');
    const [dados, setDados]           = useState(null);
    const [aplicados, setAplicados]   = useState({});    // key → nome da conta aplicada
    const [pulados, setPulados]       = useState(new Set());
    const [aplicandoKey, setAplicandoKey] = useState(null);
    const [editandoKey, setEditandoKey]   = useState(null);
    const [editForms, setEditForms]       = useState({});
    const [applyAll, setApplyAll]         = useState(false);

    const buscarAnalise = useCallback(async () => {
        setEtapa('loading');
        try {
            const { data } = await api.post('/api/financeiro/plano-contas/assistente-ia');
            setDados(data);
            // Pré-popula editForms com dados da sugestão (para modo manual)
            const forms = {};
            (data.sugestoes || []).forEach(s => {
                const key = `${s.categoria_origem}-${s.categoria_id}`;
                forms[key] = {
                    tipo_acao:        s.tipo_acao,
                    plano_contas_id:  s.plano_contas_id || '',
                    codigo:           s.nova_conta?.codigo    || '',
                    nome:             s.nova_conta?.nome      || s.plano_contas_nome || '',
                    tipo:             s.nova_conta?.tipo      || 'Despesa',
                    natureza:         s.nova_conta?.natureza  || 'D',
                    nivel:            s.nova_conta?.nivel     || 1,
                    pai_id:           s.nova_conta?.pai_id    || '',
                    pai_codigo:       s.nova_conta?.pai_codigo || '',
                };
            });
            setEditForms(forms);
            setEtapa(data.sugestoes?.length === 0 ? 'vazio' : 'analise');
        } catch (err) {
            console.error(err);
            toast.error('Erro ao consultar o Assistente IA.');
            setEtapa('erro');
        }
    }, []);

    useEffect(() => { buscarAnalise(); }, [buscarAnalise]);

    const handleAplicar = async (sugestao, useEditForm = false) => {
        const key = `${sugestao.categoria_origem}-${sugestao.categoria_id}`;
        setAplicandoKey(key);

        let sugestaoFinal = { ...sugestao };
        if (useEditForm && editForms[key]) {
            const ef = editForms[key];
            if (ef.tipo_acao === 'vincular') {
                sugestaoFinal = { ...sugestaoFinal, tipo_acao: 'vincular', plano_contas_id: parseInt(ef.plano_contas_id) };
            } else {
                sugestaoFinal = {
                    ...sugestaoFinal,
                    tipo_acao: 'criar_e_vincular',
                    nova_conta: {
                        codigo: ef.codigo, nome: ef.nome, tipo: ef.tipo,
                        natureza: ef.natureza, nivel: parseInt(ef.nivel),
                        pai_id: ef.pai_id ? parseInt(ef.pai_id) : null,
                        pai_codigo: ef.pai_codigo || null,
                    }
                };
            }
        }

        try {
            await api.post('/api/financeiro/plano-contas/aplicar-sugestao', {
                sugestao: sugestaoFinal,
                categoriaId: sugestao.categoria_id,
                categoriaOrigem: sugestao.categoria_origem,
            });
            const contaNome = sugestaoFinal.nova_conta?.nome || sugestaoFinal.plano_contas_nome || 'vinculada';
            setAplicados(prev => ({ ...prev, [key]: contaNome }));
            setEditandoKey(null);
            onAplicado();
            toast.success(`✓ ${sugestao.categoria_nome} classificada!`);
        } catch (err) {
            toast.error('Erro ao aplicar classificação.');
        } finally {
            setAplicandoKey(null);
        }
    };

    const handleAplicarTodosAlta = async () => {
        setApplyAll(true);
        const altas = (dados?.sugestoes || []).filter(s => {
            const key = `${s.categoria_origem}-${s.categoria_id}`;
            return s.confianca === 'alta' && !aplicados[key] && !pulados.has(key);
        });
        for (const s of altas) { await handleAplicar(s); }
        setApplyAll(false);
    };

    const totalSugestoes = dados?.sugestoes?.length || 0;
    const totalAplicados = Object.keys(aplicados).length;
    const totalAlta      = (dados?.sugestoes || []).filter(s => s.confianca === 'alta').length;
    const progresso      = totalSugestoes > 0 ? Math.round((totalAplicados / totalSugestoes) * 100) : 0;

    return (
        <>
            {/* Backdrop */}
            <div
                onClick={onClose}
                style={{
                    position: 'fixed', inset: 0,
                    background: 'rgba(2, 6, 23, 0.75)',
                    backdropFilter: 'blur(3px)',
                    zIndex: 9000,
                    animation: 'fadeIn 0.2s ease',
                }}
            />

            {/* Drawer */}
            <div style={{
                position: 'fixed', top: 0, right: 0, bottom: 0,
                width: 680, zIndex: 9001,
                display: 'flex', flexDirection: 'column',
                background: '#0f172a',
                boxShadow: '-8px 0 40px rgba(0,0,0,0.5)',
                animation: 'slideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                fontFamily: "'system-ui', sans-serif",
            }}>

                {/* ── Header ─────────────────────────────────────────────── */}
                <div style={{
                    padding: '1.25rem 1.5rem',
                    borderBottom: '1px solid #1e293b',
                    background: '#0a1628',
                    flexShrink: 0,
                }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{
                                width: 40, height: 40, borderRadius: 10,
                                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0,
                            }}>
                                <FaRobot style={{ color: 'white', fontSize: 18 }} />
                            </div>
                            <div>
                                <div style={{ color: 'white', fontWeight: 700, fontSize: '1rem', letterSpacing: '-0.02em' }}>
                                    Assistente Contábil IA
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: etapa === 'analise' ? '#10b981' : '#6366f1', animation: etapa === 'loading' ? 'pulse 1.5s infinite' : 'none' }} />
                                    <span style={{ color: '#94a3b8', fontSize: '0.75rem', fontWeight: 500 }}>
                                        Gemini 2.5 Flash · Normas NBC/CFC
                                    </span>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            style={{
                                background: '#1e293b', border: '1px solid #334155',
                                borderRadius: 8, padding: '6px 8px',
                                color: '#94a3b8', cursor: 'pointer', lineHeight: 1,
                            }}
                        >
                            <FaTimes style={{ fontSize: 13 }} />
                        </button>
                    </div>

                    {/* Barra de progresso */}
                    {etapa === 'analise' && totalSugestoes > 0 && (
                        <div style={{ marginTop: '1rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                <span style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 600 }}>
                                    PROGRESSO
                                </span>
                                <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>
                                    {totalAplicados} de {totalSugestoes} classificados
                                </span>
                            </div>
                            <div style={{ height: 4, background: '#1e293b', borderRadius: 99, overflow: 'hidden' }}>
                                <div style={{
                                    height: '100%', background: '#6366f1',
                                    width: `${progresso}%`,
                                    transition: 'width 0.5s ease',
                                    borderRadius: 99,
                                }} />
                            </div>
                            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                                <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
                                    <span style={{ color: '#10b981', fontWeight: 700 }}>{totalAlta}</span> alta confiança
                                </span>
                                <span style={{ color: '#334155' }}>·</span>
                                <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
                                    <span style={{ color: '#94a3b8', fontWeight: 700 }}>{totalSugestoes - totalAlta}</span> revisão manual
                                </span>
                                {totalAplicados === totalSugestoes && totalSugestoes > 0 && (
                                    <>
                                        <span style={{ color: '#334155' }}>·</span>
                                        <span style={{ fontSize: '0.72rem', color: '#10b981', fontWeight: 700 }}>✓ Concluído!</span>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Body ───────────────────────────────────────────────── */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1.5rem' }}>

                    {/* LOADING */}
                    {etapa === 'loading' && (
                        <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
                            <div style={{ fontSize: 40, marginBottom: 16, animation: 'spin 2s linear infinite', display: 'inline-block' }}>
                                <FaRobot style={{ color: '#6366f1' }} />
                            </div>
                            <div style={{ color: 'white', fontWeight: 600, fontSize: '0.95rem', marginBottom: 8 }}>
                                Consultando Gemini 2.5 Flash…
                            </div>
                            <div style={{ color: '#64748b', fontSize: '0.8rem', lineHeight: 1.6 }}>
                                Analisando categorias pendentes<br />contra sua estrutura contábil
                            </div>
                            <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'center', gap: 8 }}>
                                {[0, 1, 2].map(i => (
                                    <div key={i} style={{
                                        width: 8, height: 8, borderRadius: '50%', background: '#6366f1',
                                        animation: `bounce 1.2s ${i * 0.2}s infinite`,
                                    }} />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ERRO */}
                    {etapa === 'erro' && (
                        <div style={{ textAlign: 'center', padding: '3rem 2rem' }}>
                            <FaExclamationTriangle style={{ color: '#e11d48', fontSize: 36, marginBottom: 12 }} />
                            <div style={{ color: 'white', fontWeight: 600, marginBottom: 8 }}>Erro ao conectar ao Gemini</div>
                            <div style={{ color: '#64748b', fontSize: '0.8rem', marginBottom: '1.5rem' }}>Verifique a GEMINI_API_KEY no servidor</div>
                            <button onClick={buscarAnalise} style={{
                                background: '#6366f1', color: 'white', border: 'none',
                                borderRadius: 8, padding: '0.6rem 1.25rem',
                                cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem',
                                display: 'inline-flex', alignItems: 'center', gap: 8,
                            }}>
                                <FaSyncAlt /> Tentar Novamente
                            </button>
                        </div>
                    )}

                    {/* VAZIO */}
                    {etapa === 'vazio' && (
                        <div style={{ textAlign: 'center', padding: '3rem 2rem' }}>
                            <FaCheckCircle style={{ color: '#10b981', fontSize: 40, marginBottom: 12 }} />
                            <div style={{ color: 'white', fontWeight: 700, fontSize: '1rem', marginBottom: 8 }}>
                                Estrutura Contábil Íntegra!
                            </div>
                            <div style={{
                                background: '#0f2b1e', border: '1px solid #166534',
                                borderRadius: 10, padding: '1rem', marginTop: '1.5rem',
                                color: '#86efac', fontSize: '0.85rem', lineHeight: 1.7, textAlign: 'left',
                            }}>
                                {dados?.parecer_geral}
                            </div>
                        </div>
                    )}

                    {/* ANÁLISE */}
                    {etapa === 'analise' && dados && (
                        <>
                            {/* Parecer do Consultor */}
                            {dados.parecer_geral && (
                                <div style={{
                                    background: '#0f172a',
                                    border: '1px solid #1e3a5f',
                                    borderLeft: '3px solid #6366f1',
                                    borderRadius: 10, padding: '1rem 1.25rem',
                                    marginBottom: '1.25rem',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                        <FaRobot style={{ color: '#818cf8', fontSize: 13 }} />
                                        <span style={{ color: '#818cf8', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                                            Parecer do Consultor
                                        </span>
                                    </div>
                                    <p style={{ color: '#cbd5e1', fontSize: '0.85rem', lineHeight: 1.7, margin: 0 }}>
                                        {dados.parecer_geral}
                                    </p>
                                </div>
                            )}

                            {/* Botão aplicar todos alta confiança */}
                            {totalAlta > 0 && totalAplicados < totalSugestoes && (
                                <button
                                    onClick={handleAplicarTodosAlta}
                                    disabled={applyAll}
                                    style={{
                                        width: '100%', marginBottom: '1.25rem',
                                        padding: '0.75rem 1rem',
                                        background: applyAll ? '#1e293b' : 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                                        border: 'none', borderRadius: 10, cursor: applyAll ? 'wait' : 'pointer',
                                        color: 'white', fontWeight: 700, fontSize: '0.875rem',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                                        transition: 'opacity 0.2s',
                                        opacity: applyAll ? 0.6 : 1,
                                    }}
                                >
                                    {applyAll ? <FaSyncAlt style={{ animation: 'spin 0.8s linear infinite' }} /> : <FaCheckCircle />}
                                    {applyAll ? 'Aplicando...' : `Aplicar os ${totalAlta} de Alta Confiança automaticamente`}
                                </button>
                            )}

                            {/* Cards de sugestão */}
                            {dados.sugestoes.map((s, idx) => {
                                const key = `${s.categoria_origem}-${s.categoria_id}`;
                                const isAplicado  = !!aplicados[key];
                                const isPulado    = pulados.has(key);
                                const isEditando  = editandoKey === key;
                                const isAplicando = aplicandoKey === key;
                                const conf        = CONFIDENCE_MAP[s.confianca] || CONFIDENCE_MAP.media;
                                const origemInfo  = ORIGEM_LABEL[s.categoria_origem] || { label: s.categoria_origem.toUpperCase(), bg: '#f1f5f9', color: '#475569' };

                                return (
                                    <div
                                        key={key}
                                        style={{
                                            background: isAplicado ? '#0f2b1e' : isPulado ? '#1a1a2e' : 'white',
                                            border: `1px solid ${isAplicado ? '#166534' : isPulado ? '#334155' : '#e2e8f0'}`,
                                            borderRadius: 12, marginBottom: '0.875rem',
                                            overflow: 'hidden', transition: 'all 0.3s ease',
                                            opacity: isPulado ? 0.5 : 1,
                                        }}
                                    >
                                        {/* Card Header */}
                                        <div style={{
                                            padding: '0.875rem 1rem',
                                            borderBottom: `1px solid ${isAplicado ? '#166534' : '#f1f5f9'}`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            background: isAplicado ? '#0f2b1e' : '#f8fafc',
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <span style={{ fontWeight: 700, fontSize: '0.9rem', color: isAplicado ? '#86efac' : '#0f172a' }}>
                                                    {isAplicado && '✓ '}{s.categoria_nome}
                                                </span>
                                                <span style={{
                                                    fontSize: '0.65rem', fontWeight: 800,
                                                    padding: '2px 7px', borderRadius: 4,
                                                    background: origemInfo.bg, color: origemInfo.color,
                                                    textTransform: 'uppercase', letterSpacing: '0.06em',
                                                }}>
                                                    {origemInfo.label}
                                                </span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <div style={{ width: 7, height: 7, borderRadius: '50%', background: conf.dot }} />
                                                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: conf.color }}>
                                                    {conf.label}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Card Body */}
                                        <div style={{ padding: '0.875rem 1rem' }}>

                                            {/* Justificativa da IA */}
                                            <div style={{
                                                display: 'flex', gap: 8, marginBottom: '0.875rem',
                                                padding: '0.625rem 0.875rem',
                                                background: isAplicado ? '#052e16' : '#f0fdf4',
                                                borderRadius: 8, borderLeft: `3px solid ${conf.dot}`,
                                            }}>
                                                <span style={{ fontSize: '0.8rem', color: isAplicado ? '#86efac' : '#374151', lineHeight: 1.6 }}>
                                                    {s.justificativa}
                                                </span>
                                            </div>

                                            {/* Estado aplicado */}
                                            {isAplicado && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.5rem 0' }}>
                                                    <FaCheckCircle style={{ color: '#10b981', fontSize: 14 }} />
                                                    <span style={{ color: '#86efac', fontSize: '0.82rem', fontWeight: 600 }}>
                                                        Vinculada a: {aplicados[key]}
                                                    </span>
                                                </div>
                                            )}

                                            {/* Sugestão da IA (não aplicado) */}
                                            {!isAplicado && !isEditando && (
                                                <div style={{
                                                    background: '#f8fafc',
                                                    border: '1px solid #e2e8f0',
                                                    borderRadius: 8, padding: '0.75rem 1rem',
                                                    marginBottom: '0.875rem',
                                                }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                                                        <span style={{
                                                            fontSize: '0.65rem', fontWeight: 800,
                                                            padding: '2px 7px', borderRadius: 4,
                                                            background: s.tipo_acao === 'criar_e_vincular' ? '#ede9fe' : '#dbeafe',
                                                            color: s.tipo_acao === 'criar_e_vincular' ? '#5b21b6' : '#1e40af',
                                                            textTransform: 'uppercase', letterSpacing: '0.06em',
                                                        }}>
                                                            {s.tipo_acao === 'criar_e_vincular' ? '✦ Criar e Vincular' : '→ Vincular Existente'}
                                                        </span>
                                                    </div>
                                                    {s.tipo_acao === 'criar_e_vincular' && s.nova_conta && (
                                                        <>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                <code style={{ fontSize: '0.8rem', fontWeight: 700, color: '#4f46e5', background: '#ede9fe', padding: '2px 7px', borderRadius: 4 }}>
                                                                    {s.nova_conta.codigo}
                                                                </code>
                                                                <span style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.88rem' }}>
                                                                    {s.nova_conta.nome}
                                                                </span>
                                                            </div>
                                                            {s.nova_conta.pai_codigo && (
                                                                <div style={{ marginTop: 5, fontSize: '0.75rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4 }}>
                                                                    <FaArrowRight style={{ fontSize: 9 }} />
                                                                    Sob: {s.nova_conta.pai_codigo}
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                    {s.tipo_acao === 'vincular' && (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                            <code style={{ fontSize: '0.8rem', fontWeight: 700, color: '#1e40af', background: '#dbeafe', padding: '2px 7px', borderRadius: 4 }}>
                                                                {s.plano_contas_codigo}
                                                            </code>
                                                            <span style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.88rem' }}>
                                                                {s.plano_contas_nome}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Formulário de edição manual */}
                                            {isEditando && (
                                                <div style={{
                                                    background: '#f8fafc', border: '1px solid #c7d2fe',
                                                    borderRadius: 8, padding: '1rem',
                                                    marginBottom: '0.875rem',
                                                }}>
                                                    <div style={{ marginBottom: 8, fontSize: '0.72rem', fontWeight: 700, color: '#4f46e5', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                                        Edição Manual
                                                    </div>
                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                                                        <div>
                                                            <label style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>Ação</label>
                                                            <select
                                                                value={editForms[key]?.tipo_acao}
                                                                onChange={e => setEditForms(prev => ({ ...prev, [key]: { ...prev[key], tipo_acao: e.target.value } }))}
                                                                style={{ width: '100%', padding: '5px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.8rem', marginTop: 4 }}
                                                            >
                                                                <option value="criar_e_vincular">Criar e Vincular</option>
                                                                <option value="vincular">Vincular Existente</option>
                                                            </select>
                                                        </div>
                                                        {editForms[key]?.tipo_acao === 'vincular' ? (
                                                            <div>
                                                                <label style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>ID da Conta Existente</label>
                                                                <input
                                                                    type="number"
                                                                    value={editForms[key]?.plano_contas_id}
                                                                    onChange={e => setEditForms(prev => ({ ...prev, [key]: { ...prev[key], plano_contas_id: e.target.value } }))}
                                                                    placeholder="ex: 42"
                                                                    style={{ width: '100%', padding: '5px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.8rem', marginTop: 4, boxSizing: 'border-box' }}
                                                                />
                                                            </div>
                                                        ) : (
                                                            <div>
                                                                <label style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>Código</label>
                                                                <input
                                                                    value={editForms[key]?.codigo}
                                                                    onChange={e => setEditForms(prev => ({ ...prev, [key]: { ...prev[key], codigo: e.target.value } }))}
                                                                    placeholder="ex: 4.1.03.01"
                                                                    style={{ width: '100%', padding: '5px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.8rem', marginTop: 4, boxSizing: 'border-box' }}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                    {editForms[key]?.tipo_acao === 'criar_e_vincular' && (
                                                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8 }}>
                                                            <div>
                                                                <label style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>Nome da Conta</label>
                                                                <input
                                                                    value={editForms[key]?.nome}
                                                                    onChange={e => setEditForms(prev => ({ ...prev, [key]: { ...prev[key], nome: e.target.value } }))}
                                                                    style={{ width: '100%', padding: '5px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.8rem', marginTop: 4, boxSizing: 'border-box' }}
                                                                />
                                                            </div>
                                                            <div>
                                                                <label style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>Tipo</label>
                                                                <select
                                                                    value={editForms[key]?.tipo}
                                                                    onChange={e => setEditForms(prev => ({ ...prev, [key]: { ...prev[key], tipo: e.target.value } }))}
                                                                    style={{ width: '100%', padding: '5px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.8rem', marginTop: 4 }}
                                                                >
                                                                    <option>Ativo</option><option>Passivo</option>
                                                                    <option>Receita</option><option>Despesa</option>
                                                                    <option>Patrimônio Líquido</option>
                                                                </select>
                                                            </div>
                                                            <div>
                                                                <label style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>Natureza</label>
                                                                <select
                                                                    value={editForms[key]?.natureza}
                                                                    onChange={e => setEditForms(prev => ({ ...prev, [key]: { ...prev[key], natureza: e.target.value } }))}
                                                                    style={{ width: '100%', padding: '5px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.8rem', marginTop: 4 }}
                                                                >
                                                                    <option value="D">Devedora (D)</option>
                                                                    <option value="C">Credora (C)</option>
                                                                </select>
                                                            </div>
                                                            <div>
                                                                <label style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>Código do Pai</label>
                                                                <input
                                                                    value={editForms[key]?.pai_codigo}
                                                                    onChange={e => setEditForms(prev => ({ ...prev, [key]: { ...prev[key], pai_codigo: e.target.value } }))}
                                                                    placeholder="ex: 4.1.03"
                                                                    style={{ width: '100%', padding: '5px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.8rem', marginTop: 4, boxSizing: 'border-box' }}
                                                                />
                                                            </div>
                                                            <div>
                                                                <label style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>Nível</label>
                                                                <input
                                                                    type="number" min={1} max={9}
                                                                    value={editForms[key]?.nivel}
                                                                    onChange={e => setEditForms(prev => ({ ...prev, [key]: { ...prev[key], nivel: e.target.value } }))}
                                                                    style={{ width: '100%', padding: '5px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.8rem', marginTop: 4, boxSizing: 'border-box' }}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Ações do card */}
                                            {!isAplicado && (
                                                <div style={{ display: 'flex', gap: 6 }}>
                                                    {!isEditando ? (
                                                        <>
                                                            <button
                                                                onClick={() => handleAplicar(s)}
                                                                disabled={isAplicando}
                                                                style={{
                                                                    flex: 1, padding: '0.55rem 0',
                                                                    background: isAplicando ? '#d1fae5' : '#10b981',
                                                                    border: 'none', borderRadius: 8,
                                                                    color: 'white', fontWeight: 700, fontSize: '0.82rem',
                                                                    cursor: isAplicando ? 'wait' : 'pointer',
                                                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                                                                    transition: 'all 0.2s',
                                                                }}
                                                            >
                                                                {isAplicando
                                                                    ? <><FaSyncAlt style={{ animation: 'spin 0.8s linear infinite' }} /> Aplicando…</>
                                                                    : <><FaCheck /> Aplicar Sugestão IA</>}
                                                            </button>
                                                            <button
                                                                onClick={() => setEditandoKey(key)}
                                                                style={{
                                                                    padding: '0.55rem 0.75rem',
                                                                    background: 'white', border: '1px solid #e2e8f0',
                                                                    borderRadius: 8, color: '#475569',
                                                                    cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                                                                    display: 'flex', alignItems: 'center', gap: 6,
                                                                }}
                                                                title="Editar manualmente"
                                                            >
                                                                <FaPencilAlt style={{ fontSize: 11 }} /> Manual
                                                            </button>
                                                            {!isPulado && (
                                                                <button
                                                                    onClick={() => setPulados(prev => new Set([...prev, key]))}
                                                                    style={{
                                                                        padding: '0.55rem 0.75rem',
                                                                        background: 'white', border: '1px solid #e2e8f0',
                                                                        borderRadius: 8, color: '#94a3b8',
                                                                        cursor: 'pointer', fontSize: '0.8rem',
                                                                    }}
                                                                    title="Pular esta sugestão"
                                                                >
                                                                    Pular
                                                                </button>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button
                                                                onClick={() => handleAplicar(s, true)}
                                                                disabled={isAplicando}
                                                                style={{
                                                                    flex: 1, padding: '0.55rem 0',
                                                                    background: '#4f46e5', border: 'none',
                                                                    borderRadius: 8, color: 'white',
                                                                    fontWeight: 700, fontSize: '0.82rem',
                                                                    cursor: 'pointer', display: 'flex',
                                                                    alignItems: 'center', justifyContent: 'center', gap: 6,
                                                                }}
                                                            >
                                                                <FaSave /> Salvar Edição Manual
                                                            </button>
                                                            <button
                                                                onClick={() => setEditandoKey(null)}
                                                                style={{
                                                                    padding: '0.55rem 0.75rem',
                                                                    background: 'white', border: '1px solid #e2e8f0',
                                                                    borderRadius: 8, color: '#94a3b8',
                                                                    cursor: 'pointer', fontSize: '0.8rem',
                                                                }}
                                                            >
                                                                Cancelar
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}

                            {/* Footer — reanalisar */}
                            <button
                                onClick={buscarAnalise}
                                style={{
                                    width: '100%', marginTop: '0.5rem',
                                    padding: '0.6rem', background: 'transparent',
                                    border: '1px solid #1e293b', borderRadius: 8,
                                    color: '#475569', cursor: 'pointer', fontSize: '0.8rem',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                }}
                            >
                                <FaSyncAlt style={{ fontSize: 11 }} /> Reanalisar com IA
                            </button>
                        </>
                    )}
                </div>
            </div>

            <style>{`
                @keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
                @keyframes fadeIn  { from { opacity: 0; } to { opacity: 1; } }
                @keyframes spin    { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                @keyframes pulse   { 0%,100% { opacity:1; } 50% { opacity:0.3; } }
                @keyframes bounce  { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
            `}</style>
        </>
    );
}

// =============================================================================
// PLANO DE CONTAS PRINCIPAL
// =============================================================================
export default function PlanoDeContas() {
    const location = useLocation();
    const navigate = useNavigate();

    // --- ESTADOS DE DADOS ---
    const [contas, setContas]           = useState([]);
    const [loading, setLoading]         = useState(true);
    const [selectedIds, setSelectedIds] = useState([]);
    const [searchTerm, setSearchTerm]   = useState('');

    // --- ESTADOS VÍNCULOS ---
    const [todasCategorias, setTodasCategorias] = useState([]);
    const [currentUser, setCurrentUser]         = useState(null);

    // --- ASSISTENTE IA ---
    const [showAssistente, setShowAssistente] = useState(false);

    // --- UI ---
    const [showModal, setShowModal]   = useState(false);
    const [isSaving, setIsSaving]     = useState(false);
    const [expandedIds, setExpandedIds] = useState([]);
    const fileInputRef = useRef(null);

    // --- FORMULÁRIO ---
    const [formData, setFormData] = useState({
        id: null, codigo: '', nome: '', tipo: 'Despesa', natureza: 'D', nivel: 1, pai_id: null
    });

    const carregarDadosIniciais = useCallback(async () => {
        setLoading(true);
        try {
            const storedUser = localStorage.getItem('user');
            if (storedUser) setCurrentUser(JSON.parse(storedUser));

            const [resContas, resPagar, resReceber] = await Promise.all([
                api.get('/api/financeiro/plano-contas'),
                api.get('/api/gastos/categorias'),
                api.get('/api/financeiro/categorias-receita'),
            ]);

            const listaContas = Array.isArray(resContas.data) ? resContas.data : [];
            setContas(listaContas);

            const idsIniciais = listaContas.filter(c => c.nivel <= 2).map(c => c.id);
            setExpandedIds(idsIniciais);

            setTodasCategorias([
                ...(resPagar.data  || []).map(c => ({ ...c, origem: 'Pagar'   })),
                ...(resReceber.data|| []).map(c => ({ ...c, origem: 'Receber' })),
            ]);
        } catch (err) {
            console.error('Erro na carga:', err);
            toast.error('Erro ao sincronizar hierarquia.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        carregarDadosIniciais();
    }, [carregarDadosIniciais]);

    // Abre Assistente automaticamente se vier do DRE/Balancete
    useEffect(() => {
        if (location.state?.abrirAssistente) {
            setShowAssistente(true);
            // Limpa o state para não re-abrir no próximo render
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    const canManageVinculos = useMemo(() => (
        ['admin_geral', 'admin', 'financeiro'].includes(currentUser?.perfil)
    ), [currentUser]);

    // Contagem de categorias sem vínculo para o botão do header
    const semVinculoCount = useMemo(() => (
        todasCategorias.filter(c => !c.plano_contas_id_padrao).length
    ), [todasCategorias]);

    // ── GAVETA ─────────────────────────────────────────────────────────────
    const toggleExpand = (e, id) => {
        e.stopPropagation();
        setExpandedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    };

    const contasExibidas = useMemo(() => {
        const term = searchTerm.toLowerCase().trim();
        if (term) {
            return contas.filter(c =>
                (c.nome   && c.nome.toLowerCase().includes(term)) ||
                (c.codigo && c.codigo.includes(term))
            );
        }
        return contas.filter(conta => {
            if (conta.nivel === 1) return true;
            const partes = conta.codigo.split('.');
            let codigoParcial = '';
            for (let i = 0; i < partes.length - 1; i++) {
                codigoParcial += (i === 0 ? '' : '.') + partes[i];
                const pai = contas.find(c => c.codigo === codigoParcial);
                if (pai && !expandedIds.includes(pai.id)) return false;
            }
            return true;
        });
    }, [contas, searchTerm, expandedIds]);

    // ── VÍNCULOS ───────────────────────────────────────────────────────────
    const handleVincular = async (catJson) => {
        if (!catJson) return;
        const cat  = JSON.parse(catJson);
        const rota = cat.origem === 'Pagar' ? '/api/gastos/categorias' : '/api/financeiro/categorias-receita';
        try {
            await api.put(`${rota}/${cat.id}`, { plano_contas_id_padrao: formData.id });
            toast.success(`${cat.nome} vinculada!`);
            setTodasCategorias(prev => prev.map(c =>
                (c.id === cat.id && c.origem === cat.origem) ? { ...c, plano_contas_id_padrao: formData.id } : c
            ));
        } catch { toast.error('Erro ao vincular.'); }
    };

    const handleDesvincular = async (cat) => {
        if (!window.confirm('Remover vínculo?')) return;
        const rota = cat.origem === 'Pagar' ? '/api/gastos/categorias' : '/api/financeiro/categorias-receita';
        try {
            await api.put(`${rota}/${cat.id}`, { plano_contas_id_padrao: null });
            setTodasCategorias(prev => prev.map(c =>
                (c.id === cat.id && c.origem === cat.origem) ? { ...c, plano_contas_id_padrao: null } : c
            ));
        } catch { toast.error('Erro ao desvincular.'); }
    };

    // ── SELEÇÃO / EXCLUSÃO ─────────────────────────────────────────────────
    const handleToggleSelect = (e, conta) => {
        e.stopPropagation();
        const isSelected = selectedIds.includes(conta.id);
        const targets = contas.filter(c => c.codigo === conta.codigo || c.codigo.startsWith(`${conta.codigo}.`)).map(c => c.id);
        setSelectedIds(prev => isSelected ? prev.filter(id => !targets.includes(id)) : [...new Set([...prev, ...targets])]);
    };

    const handleSelectAll = (e) => {
        setSelectedIds(e.target.checked ? contasExibidas.map(c => c.id) : []);
    };

    const handleBulkDelete = async () => {
        if (!selectedIds.length) return;
        if (!window.confirm(`Excluir permanentemente ${selectedIds.length} contas?`)) return;
        setLoading(true);
        try {
            await api.delete('/api/financeiro/plano-contas/bulk', { data: { ids: selectedIds } });
            toast.success('Exclusão concluída.');
            setSelectedIds([]);
            carregarDadosIniciais();
        } catch { toast.error('Erro na exclusão.'); } finally { setLoading(false); }
    };

    const handleDeleteSingle = async (id) => {
        if (!window.confirm('Excluir conta?')) return;
        try {
            await api.delete(`/api/financeiro/plano-contas/${id}`);
            carregarDadosIniciais();
        } catch { toast.error('Erro ao excluir.'); }
    };

    // ── IMPORT / EXPORT ────────────────────────────────────────────────────
    const handleExportData = async () => {
        try {
            setLoading(true);
            const { data } = await api.get('/api/financeiro/plano-contas/exportar');
            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Plano_Contas');
            XLSX.writeFile(wb, `BLB_PlanoContas_${new Date().getFullYear()}.xlsx`);
        } catch { toast.error('Erro ao exportar.'); } finally { setLoading(false); }
    };

    const handleDownloadTemplate = () => {
        const ws = XLSX.utils.json_to_sheet([{ codigo: '1', nome: 'ATIVO', tipo: 'Ativo', natureza: 'D', nivel: 1 }]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Modelo');
        XLSX.writeFile(wb, 'Modelo_Importacao.xlsx');
    };

    const handleImportExcel = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const wb   = XLSX.read(evt.target.result, { type: 'binary' });
                const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
                setLoading(true);
                await api.post('/api/financeiro/plano-contas/importar', { rows });
                toast.success('Importado com sucesso!');
                carregarDadosIniciais();
            } catch { toast.error('Erro na importação.'); } finally { setLoading(false); e.target.value = null; }
        };
        reader.readAsBinaryString(file);
    };

    // ── MODAL ──────────────────────────────────────────────────────────────
    const handleOpenModal = (item = null, isEdit = false) => {
        if (isEdit) {
            setFormData({ ...item });
        } else if (item) {
            setFormData({ id: null, codigo: `${item.codigo}.`, nome: '', tipo: item.tipo, natureza: item.natureza, nivel: item.nivel + 1, pai_id: item.id });
        } else {
            setFormData({ id: null, codigo: '', nome: '', tipo: 'Despesa', natureza: 'D', nivel: 1, pai_id: null });
        }
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const method = formData.id ? 'put' : 'post';
            const url    = formData.id ? `/api/financeiro/plano-contas/${formData.id}` : '/api/financeiro/plano-contas';
            await api[method](url, formData);
            setShowModal(false);
            carregarDadosIniciais();
        } catch { toast.error('Erro ao salvar.'); } finally { setIsSaving(false); }
    };

    const summary = useMemo(() => ({
        total:     contas.length,
        ativo:     contas.filter(c => c.tipo === 'Ativo').length,
        passivo:   contas.filter(c => c.tipo === 'Passivo').length,
        resultado: contas.filter(c => ['Receita', 'Despesa'].includes(c.tipo)).length,
    }), [contas]);

    // ═══════════════════════════════════════════════════════════════════════
    return (
        <div className="pc-master-wrapper animate-fade-in">

            {/* ASSISTENTE IA DRAWER */}
            {showAssistente && (
                <AssistentePainel
                    onClose={() => setShowAssistente(false)}
                    onAplicado={carregarDadosIniciais}
                />
            )}

            {/* HEADER */}
            <header className="pc-main-header">
                <div className="pc-brand-section">
                    <div className="pc-icon-circle"><FaSitemap /></div>
                    <div>
                        <h1>Plano de Contas Contábil</h1>
                        <p>Gestão Estrutural Consolidada</p>
                    </div>
                </div>

                <div className="pc-header-actions">
                    <div className="pc-search-box">
                        <FaSearch />
                        <input
                            type="text" placeholder="Buscar..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="pc-main-btns">
                        {/* BOTÃO ASSISTENTE IA — destaque especial */}
                        <button
                            className="pc-btn"
                            onClick={() => setShowAssistente(true)}
                            style={{
                                background: semVinculoCount > 0
                                    ? 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)'
                                    : 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                                color: 'white', border: 'none',
                                display: 'flex', alignItems: 'center', gap: 8,
                                boxShadow: semVinculoCount > 0
                                    ? '0 0 16px rgba(99,102,241,0.4)'
                                    : '0 0 16px rgba(16,185,129,0.3)',
                                fontWeight: 700, padding: '0.55rem 1rem',
                            }}
                        >
                            <FaRobot style={{ fontSize: 14 }} />
                            {semVinculoCount > 0
                                ? `${semVinculoCount} pendente${semVinculoCount > 1 ? 's' : ''} · Classificar com IA`
                                : '✓ Tudo Classificado'}
                        </button>

                        {selectedIds.length > 0 && (
                            <button className="pc-btn pc-btn-danger" onClick={handleBulkDelete}>
                                <FaTrash /> Excluir ({selectedIds.length})
                            </button>
                        )}
                        <button className="pc-btn pc-btn-success" onClick={handleExportData}><FaFileExcel /> Exportar</button>
                        <button className="pc-btn pc-btn-outline" onClick={handleDownloadTemplate}><FaDownload /> Modelo</button>
                        <button className="pc-btn pc-btn-outline" onClick={() => fileInputRef.current.click()}><FaUpload /> Importar</button>
                        <input type="file" ref={fileInputRef} hidden onChange={handleImportExcel} accept=".xlsx,.xls" />
                        <button className="pc-btn pc-btn-primary" onClick={() => handleOpenModal()}><FaPlus /> Nova Raiz</button>
                    </div>
                </div>
            </header>

            {/* DASHBOARD */}
            <div className="pc-audit-dashboard">
                <div className="pc-audit-card">
                    <FaChartLine />
                    <div><small>Estrutura Total</small><strong>{summary.total} Contas</strong></div>
                </div>
                <div className="pc-audit-card blue">
                    <div className="pc-dot" />
                    <div><small>Grupo Ativo</small><strong>{summary.ativo}</strong></div>
                </div>
                <div className="pc-audit-card red">
                    <div className="pc-dot" />
                    <div><small>Grupo Passivo</small><strong>{summary.passivo}</strong></div>
                </div>
                <div className="pc-audit-card green">
                    <div className="pc-dot" />
                    <div><small>Resultado</small><strong>{summary.resultado}</strong></div>
                </div>
                {/* Card de pendências */}
                {semVinculoCount > 0 && (
                    <div
                        className="pc-audit-card"
                        onClick={() => setShowAssistente(true)}
                        style={{ cursor: 'pointer', borderColor: '#818cf8', background: 'rgba(99,102,241,0.08)' }}
                    >
                        <FaExclamationTriangle style={{ color: '#f59e0b' }} />
                        <div>
                            <small style={{ color: '#f59e0b' }}>Sem Vínculo</small>
                            <strong style={{ color: '#818cf8' }}>{semVinculoCount} Categ.</strong>
                        </div>
                    </div>
                )}
            </div>

            {/* TABELA */}
            <div className="pc-content-card">
                <div className="pc-table-container">
                    <table className="pc-main-table">
                        <thead>
                            <tr>
                                <th width="45" style={{ textAlign: 'center' }}>
                                    <input type="checkbox" onChange={handleSelectAll} checked={selectedIds.length === contasExibidas.length && contasExibidas.length > 0} />
                                </th>
                                <th width="160">Código Estrutural</th>
                                <th>Descrição da Conta</th>
                                <th width="160">Classificação</th>
                                <th width="130">Natureza</th>
                                <th width="100" style={{ textAlign: 'center' }}>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="6" className="pc-loading-state"><div className="pc-spinner" /><span>Sincronizando...</span></td></tr>
                            ) : contasExibidas.length > 0 ? (
                                contasExibidas.map(conta => {
                                    const temFilhos  = contas.some(c => c.pai_id === conta.id);
                                    const isExpanded = expandedIds.includes(conta.id);
                                    return (
                                        <tr
                                            key={conta.id}
                                            className={`pc-row pc-lv-${conta.nivel} ${selectedIds.includes(conta.id) ? 'pc-selected' : ''}`}
                                            onClick={(e) => handleToggleSelect(e, conta)}
                                        >
                                            <td style={{ textAlign: 'center' }}>
                                                <input type="checkbox" checked={selectedIds.includes(conta.id)} readOnly />
                                            </td>
                                            <td className="pc-td-code"><code>{conta.codigo}</code></td>
                                            <td className="pc-td-name" style={{ paddingLeft: `${(conta.nivel - 1) * 28 + 15}px` }}>
                                                {temFilhos ? (
                                                    <div className={`pc-expander ${isExpanded ? 'open' : ''}`} onClick={(e) => toggleExpand(e, conta.id)}>
                                                        {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                                                    </div>
                                                ) : <div style={{ width: 24 }} />}
                                                {conta.nivel === 1 ? <FaRegFolder className="pc-icon-folder" /> : <FaRegFileAlt className="pc-icon-file" />}
                                                <span className={conta.nivel === 1 ? 'pc-weight-bold' : ''}>{conta.nome}</span>
                                            </td>
                                            <td><span className={`pc-badge pc-badge-${conta.tipo.toLowerCase().replace(/\s/g, '-')}`}>{conta.tipo}</span></td>
                                            <td><span className={`pc-natureza ${conta.natureza === 'D' ? 'devedora' : 'credora'}`}>{conta.natureza === 'D' ? 'Devedora (+)' : 'Credora (-)'}</span></td>
                                            <td className="pc-td-actions">
                                                <button className="pc-action-btn edit" onClick={(e) => { e.stopPropagation(); handleOpenModal(conta, true); }} title="Editar / Vínculos"><FaHistory /></button>
                                                <button className="pc-action-btn add"  onClick={(e) => { e.stopPropagation(); handleOpenModal(conta); }} title="Subconta"><FaPlus /></button>
                                                <button className="pc-action-btn del"  onClick={(e) => { e.stopPropagation(); handleDeleteSingle(conta.id); }} title="Excluir"><FaTrash /></button>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr><td colSpan="6" className="pc-empty-state"><FaInfoCircle /> Nenhuma conta localizada.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* MODAL DE CONTA */}
            {showModal && (
                <div className="pc-modal-overlay">
                    <div className="pc-modal animate-pop-in">
                        <div className="pc-modal-header">
                            <h3><FaLayerGroup /> {formData.id ? 'Editar Conta' : (formData.pai_id ? 'Anexar Subconta' : 'Registrar Conta Raiz')}</h3>
                            <button className="pc-close" onClick={() => setShowModal(false)}><FaTimes /></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="pc-modal-body">
                                <div className="pc-form-row">
                                    <div className="pc-input-group">
                                        <label>Código Hierárquico</label>
                                        <input required type="text" value={formData.codigo} onChange={e => setFormData({ ...formData, codigo: e.target.value })} />
                                    </div>
                                    <div className="pc-input-group">
                                        <label>Descrição da Conta</label>
                                        <input required type="text" value={formData.nome} onChange={e => setFormData({ ...formData, nome: e.target.value })} />
                                    </div>
                                </div>
                                <div className="pc-form-row">
                                    <div className="pc-input-group">
                                        <label>Grupo Contábil</label>
                                        <select value={formData.tipo} onChange={e => setFormData({ ...formData, tipo: e.target.value })}>
                                            <option value="Ativo">Ativo</option>
                                            <option value="Passivo">Passivo</option>
                                            <option value="Patrimônio Líquido">Patrimônio Líquido</option>
                                            <option value="Receita">Receita</option>
                                            <option value="Despesa">Despesa</option>
                                        </select>
                                    </div>
                                    <div className="pc-input-group">
                                        <label>Natureza (D/C)</label>
                                        <select value={formData.natureza} onChange={e => setFormData({ ...formData, natureza: e.target.value })}>
                                            <option value="D">Devedora (+)</option>
                                            <option value="C">Credora (-)</option>
                                        </select>
                                    </div>
                                </div>

                                {formData.id && (
                                    <div className="pc-vinculos-container">
                                        <hr />
                                        <h4><FaLink /> Categorias Operacionais Vinculadas</h4>
                                        <div className="pc-vinculos-list">
                                            {todasCategorias.filter(c => c.plano_contas_id_padrao === formData.id).map(cat => (
                                                <div key={`${cat.origem}-${cat.id}`} className="pc-tag-vinculo">
                                                    <span>{cat.nome} <small>({cat.origem})</small></span>
                                                    {canManageVinculos && <button type="button" onClick={() => handleDesvincular(cat)}><FaTimes /></button>}
                                                </div>
                                            ))}
                                            {todasCategorias.filter(c => c.plano_contas_id_padrao === formData.id).length === 0 && (
                                                <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Nenhuma categoria vinculada.</span>
                                            )}
                                        </div>
                                        {canManageVinculos && (
                                            <div className="pc-add-vinculo-row">
                                                <select onChange={(e) => handleVincular(e.target.value)} value="">
                                                    <option value="">+ Vincular Categoria Operacional</option>
                                                    {todasCategorias.filter(c => c.plano_contas_id_padrao !== formData.id).map(cat => (
                                                        <option key={`${cat.origem}-${cat.id}`} value={JSON.stringify(cat)}>{cat.nome} ({cat.origem})</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="pc-modal-warning">
                                    <FaExclamationTriangle />
                                    <span>Alterações estruturais afetam o balanço patrimonial e DRE. Use com cautela.</span>
                                </div>
                            </div>
                            <div className="pc-modal-footer">
                                <button type="button" className="pc-btn-cancel" onClick={() => setShowModal(false)}>Cancelar</button>
                                <button type="submit" className="pc-btn-save" disabled={isSaving}>
                                    {isSaving ? 'Salvando...' : <><FaSave /> Salvar Registro</>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
